import React, { useState, useRef, useEffect } from "react";
import "./DynamicToolTip.css";

export const DynamicToolTip = ({
  children,
  content = "",
  onClick,
  position = "top",
  customClassName = "",
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [calculatedPosition, setCalculatedPosition] = useState(position);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  // Function to calculate best position based on viewport
  const calculateBestPosition = () => {
    if (!triggerRef.current) return position;

    const triggerRect = triggerRef.current.getBoundingClientRect(); // FIXED: triggerRef not triggerRect
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Estimate tooltip size (you can adjust these values)
    const tooltipWidth = 200;
    const tooltipHeight = 80;

    // Calculate available space for each position
    const spaceAvailable = {
      top: triggerRect.top - tooltipHeight - 20, // Space above trigger
      bottom: viewport.height - triggerRect.bottom - tooltipHeight - 20, // Space below trigger
      left: triggerRect.left - tooltipWidth - 20, // Space left of trigger
      right: viewport.width - triggerRect.right - tooltipWidth - 20, // Space right of trigger
    };

    // Check if preferred position has enough space
    if (spaceAvailable[position] > 20) {
      return position;
    }

    // Find the position with most space
    let bestPosition = position;
    let maxSpace = -Infinity;

    Object.keys(spaceAvailable).forEach((pos) => {
      if (spaceAvailable[pos] > maxSpace) {
        maxSpace = spaceAvailable[pos];
        bestPosition = pos;
      }
    });

    // If no position has enough space, default to top
    return maxSpace > 20 ? bestPosition : "top";
  };

  useEffect(() => {
    if (showTooltip) {
      const bestPos = calculateBestPosition();
      setCalculatedPosition(bestPos);
    }
  }, [showTooltip]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setShowTooltip(false);
      }
    };

    const handleResize = () => {
      if (showTooltip) {
        const bestPos = calculateBestPosition();
        setCalculatedPosition(bestPos);
      }
    };

    const handleScroll = () => {
      if (showTooltip) {
        const bestPos = calculateBestPosition();
        setCalculatedPosition(bestPos);
      }
    };

    document.addEventListener("click", handleClickOutside);
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showTooltip]);

  const handleTooltipClick = (event) => {
    event.stopPropagation();
    setShowTooltip(!showTooltip);

    if (onClick) {
      onClick(content);
    }
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <div className="tooltip-container">
      <div
        ref={triggerRef}
        className="tooltip-trigger"
        onClick={handleTooltipClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {showTooltip && content && (
        <div
          ref={tooltipRef}
          className={`dynamic-tooltip tooltip-${calculatedPosition} ${customClassName}`}
        >
          <p>{content}</p>
          <div className={`arrow arrow-${calculatedPosition}`}></div>
        </div>
      )}
    </div>
  );
};
